import { ProfileView } from "../../../services/ProfileService/types/profileTypes";

// Theme constants
export const THEME_COLORS = {
  primary: '#162346',
  primaryHover: '#1a2a52',
  background: '#f8fafc',
  card: '#ffffff',
  text: {
    primary: '#1e293b',
    secondary: 'text.secondary'
  }
} as const;

// Role constants
export const ADMIN_ROLES = {
  ADMINISTRATOR: "Administrator",
  REV_CYCLE_ADMIN: "Rev Cycle Administrator"
} as const;

// Permission utilities
export const hasAdminAccess = (profile?: ProfileView): boolean => {
  if (!profile?.role) return false;
  return profile.role === ADMIN_ROLES.ADMINISTRATOR || profile.role === ADMIN_ROLES.REV_CYCLE_ADMIN;
};

export const canEditProfile = (profile?: ProfileView): boolean => {
  return profile?.isAdmin === true;
};

// Navigation menu access utilities
export const hasConfigurationAccess = (profile?: ProfileView): boolean => {
  if (!profile?.role) return false;
  return profile.role === ADMIN_ROLES.ADMINISTRATOR || profile.role === ADMIN_ROLES.REV_CYCLE_ADMIN;
};

export const hasClaimAccess = (claimValue: string, profile?: ProfileView): boolean => {
  if (!profile?.claimsAccess) return false;
  return profile.claimsAccess.some(claim => 
    claim.value === claimValue || claim.name === claimValue
  );
};

// Specific claim access checks
export const hasSchedulingAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('Scheduling', profile);
};

// The OR surgery board's administrators, who may change a facility's conflict settings.
export const hasSchedulingAdminAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('SchedulingAdmin', profile);
};

export const hasInsuranceVerifyAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('InsuranceVerify', profile);
};

export const hasAdecAuditAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('AdecAudit', profile);
};

export const hasVobManagerAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('VobManager', profile);
};

export const hasCaseManagementAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('CaseManagement', profile);
};

export const hasClinicalPolicyAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('Policies', profile);
};

export const hasRefundsAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('RefundEdit', profile);
};

export const hasWriteoffsAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('WriteoffEdit', profile);
};

export const hasContractWriteAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('ContractWrite', profile);
};

export const hasAppealsAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('Appeals', profile);
};

export const hasLaborManagementAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('LaborManagement', profile);
};

export const hasPhysicianGroupAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('PhysicianGroupEdit', profile);
};

export const hasLaborManagementAdminAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('LaborManagementAdmin', profile);
};

export const hasMedNecAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('MedNec', profile);
};

export const hasAgentsViewAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('AgentView', profile);
};

export const hasCamEditAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('CamEdit', profile);
};

export const hasMedicalRecordsAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('MedicalRecords', profile);
};

export const hasPhysicianRequestsAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('PhysicianRequests', profile);
};

export const hasRevRecAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('RevRec', profile);
};

export const hasDenialsAccess = (profile?: ProfileView): boolean => {
  return hasClaimAccess('Denials', profile);
};

// Data transformation utilities
export const transformClaimsAccess = (claimsAccess: any[] | undefined) => {
  if (!claimsAccess) return [];
  
  return Array.isArray(claimsAccess) && typeof claimsAccess[0] === "string"
    ? (claimsAccess as unknown as string[]).map((c) => ({ value: c, name: c }))
    : (claimsAccess as any[]).map((c) => ({
        value: c.value ?? c.claimValue ?? String(c),
        name: c.name ?? c.claimName ?? String(c),
      }));
};

export const transformAvailableClaims = (availableClaimsData: any[] | undefined) => {
  return availableClaimsData ? availableClaimsData.map(c => ({ 
    value: c.value, 
    name: c.name,
    module: c.module,
    description: c.description
  })) : [];
};

export const transformAvailableCenters = (availableCentersData: any[] | undefined) => {
  return availableCentersData 
    ? availableCentersData.map((c: any) => ({ 
        code: c.code, 
        name: c.shortName && c.shortName.trim() !== "" ? c.shortName : c.name 
      })) 
    : [];
};
